<script setup>
    import MainHeader from './../../components/HeaderComponent.vue';
    import { onMounted } from 'vue';
    import { getCurrentInstance } from 'vue';
    import { useRouter } from 'vue-router';
    import { inject, reactive } from 'vue';
    import { useSigninResetStore } from '@/stores/signin/signinResetStore';
    import { signinResetApplyFormInfo } from '@/models/signinModel';
    import BaseInput from './../../components/BaseInputComponent.vue';
    import Utils from '../../commonProject/helpers/utils.js';
    import i18n from '@/common/i18n';
    import { useLoginStore } from '@/stores/login/loginStore';
    import { useButtonDisabledStore } from '@/stores/common/disabledStore';

    // ----------------------------
    // 定数
    // ----------------------------
    const app = getCurrentInstance();

    // メッセージダイアログ取得。
    const { messageDialog } = inject('messageDialogKey');

    const signinResetApplyForm = reactive(new signinResetApplyFormInfo());
    const router = useRouter();
    const loginStore = useLoginStore();
    const signinResetStore = useSigninResetStore();

    // ボタン連打ガード制御
    const disabledStore = useButtonDisabledStore();

    onMounted(async () => {
        // Storeを初期化する。
        signinResetStore.reset();
    });

    /**
     * パスワードリセットID入力
     */
    async function sendMailPasswordReset() {
        // バリデーションチェック
        if (isValidationError()) {
            return;
        }

        // ボタン連打ガード制御
        disabledStore.buttonTemporaryDisabled();

        // パスワードリセット1API用のリクエストボディを生成する。
        const requestBody = {
            VendorId: loginStore.getVendorId,
            CmpId: signinResetApplyForm.companyId,
            UserId: signinResetApplyForm.userId,
        };

        // パスワードリセット1APIをコールする。
        const response = await signinResetStore.sendPasswordResetAction(app.appContext.config.globalProperties.$constProperties.BASE_API_URL_P, requestBody);

        if (response.success) {
            if (!Utils.isNullEmpty(response.responseBody.PwdrstPk)) {
                // パスワードリセット履歴PKを記録する。
                signinResetStore.pwdrstPk = response.responseBody.PwdrstPk;
            }

            // パスワードリセット新パスワード入力画面へ遷移する。
            router.push({ name: 'signinResetPassword' });
        } else {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
            return;
        }
    }
    /**
     * バリデーションチェック
     */
    function isValidationError() {
        // エラーメッセージを初期化する。
        let message = '';

        // 企業ID
        if (Utils.isNullEmpty(signinResetApplyForm.companyId)) {
            message += i18n.global.t('signinResetApply.message.message1') + '\n';
        } else {
            if (Utils.checkLengthOfCompanyId(signinResetApplyForm.companyId)) {
                message += i18n.global.t('signinResetApply.message.message2') + '\n';
            }
            if (!Utils.isMatchPatternAlphabetNumber(signinResetApplyForm.companyId)) {
                message += i18n.global.t('signinResetApply.message.message3') + '\n';
            }
        }
        // ユーザーID
        if (Utils.isNullEmpty(signinResetApplyForm.userId)) {
            message += i18n.global.t('signinResetApply.message.message4') + '\n';
        } else {
            if (Utils.checkLengthOfUserId(signinResetApplyForm.userId)) {
                message += i18n.global.t('signinResetApply.message.message5') + '\n';
            }
            if (!Utils.isMatchPatternLowerAlphabetNumber(signinResetApplyForm.userId)) {
                message += i18n.global.t('signinResetApply.message.message6') + '\n';
            }
        }

        // エラーメッセージが存在する場合は、以降の処理に進ませないので、バリデーションチェック判定を返却する。
        if (!Utils.isNullEmpty(message)) {
            // エラーメッセージが存在する場合は、画面に表示する。
            messageDialog('error', message);
            return true;
        }

        return false;
    }
</script>

<template>
    <section id="main" class="login extra">
        <MainHeader />
        <div id="contents" class="large">
            <h2>{{ $t('signinResetApply.parts.pageTitle1') }}</h2>
            <h3 style="margin-bottom: 21px">{{ $t('signinResetApply.parts.pageInformation1') }}<br />{{ $t('signinResetApply.parts.pageInformation2') }}<br />{{ $t('signinResetApply.parts.pageInformation3') }}</h3>
            <section class="form">
                <dl>
                    <dt>
                        <label>{{ $t('signinResetApply.parts.dl1') }}</label>
                    </dt>
                    <dd>
                        <BaseInput v-model:input="signinResetApplyForm.companyId" type="text" />
                    </dd>
                </dl>
                <dl>
                    <dt>
                        <label>{{ $t('signinResetApply.parts.dl2') }}</label>
                    </dt>
                    <dd>
                        <BaseInput v-model:input="signinResetApplyForm.userId" type="text" />
                    </dd>
                </dl>
            </section>
            <ul class="btnArea" style="padding-top: 45px">
                <li>
                    <button type="button" class="btnPrimary" id="moveToResetNewPasswordInput" @click="sendMailPasswordReset" :disabled="disabledStore.isDisabled">{{ $t('signinResetApply.parts.button1') }}</button>
                </li>
            </ul>
        </div>
    </section>
</template>

<style></style>
